/**
 * DeliveryOps.tsx — FR-017 (V2/V3/V4) / FR-018 / T320.
 *
 * Bindings:
 *   - api.edgeNodes.listEdgeNodes      — REAL query (convex/edgeNodes.ts:77)
 *     returns the full list of delivery points with online / capacity facts.
 *   - api.backgroundTasks.listRecent   — REAL twin query (convex/backgroundTasks.ts:25)
 *     returns recent task summaries; used here as the operator-facing
 *     "recent actions" log surrogate (the genuine delivery-action log is
 *     not yet shipped — specs/exploration/stub_orphan_inventory.md §4 Twin #2
 *     marks api.deliveryLogs.listRecent as the STUB twin and bans its
 *     binding; the real twin is api.backgroundTasks.listRecent).
 *
 * FR-014 Twin #2 negative invariant: this file MUST NOT reference
 * `api.deliveryLogs.listRecent` anywhere. Enforced by
 * tests/editorial/virtualization.test.ts (V3).
 *
 * Honest-state contract (CLAUDE.md §1, §5):
 *   - Loading: skeletons.
 *   - Empty fleet: EmptyState (no fabricated rows).
 *   - Empty log: EmptyState (no fabricated rows).
 *   - The action surface presents the operator-facing decision affordances;
 *     the underlying mutations (vacate / restart / rotate access) are the
 *     known fail-closed routes the backend exposes — when the corresponding
 *     mutation lands wired here it routes through useMutationWithToast.
 *
 * Glossary-clean copy via src/i18n/operator-copy.ts. The action labels and
 * descriptions are paraphrased so the operator never sees the engineering
 * tokens "drenar", "redepliegue", "flota", "credencial de transmisión", or
 * the underlying route paths (port_sources §4.3 known leaks — replaced
 * here as the honest paraphrase).
 *
 * AdminGate wraps the whole surface (FR-015 (O-C)): only admins reach
 * delivery operations.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  AdminGate,
  Btn,
  EmptyState,
  Icon,
  PageHead,
  Panel,
  Pill,
  Skeleton,
  StatCard,
} from '../../components/primitives';
import { useCurrentMember } from '../../hooks/useCurrentMember';
import { DELIVERY_OPS_COPY } from '../../i18n/operator-copy';

interface EdgeNodeShape {
  _id?: string;
  name?: string;
  isOnline?: boolean;
  operationalStatus?: string;
  streamCapacity?: number;
  currentStreamLoad?: number;
  region?: string;
  provider?: string;
}

interface TaskSummaryShape {
  taskName?: string;
  lastRunAt?: number | null;
  lastStatus?: 'ok' | 'failed' | 'running' | string;
  avgDurationMs?: number;
}

// Honest action descriptors — labels and descriptions paraphrased from the
// engineering shape to glossary-safe operator language (port_sources §4.4).
// No "endpoint" / "drenar" / "redepliegue" / "rotar credencial de transmisión"
// surfaces in the JSX.
interface DeliveryAction {
  readonly id: string;
  readonly icon: string;
  readonly label: string;
  readonly description: string;
  readonly danger: boolean;
}

const DELIVERY_ACTIONS: readonly DeliveryAction[] = [
  {
    id: 'vacate',
    icon: 'power',
    label: DELIVERY_OPS_COPY.actionVacate,
    description: DELIVERY_OPS_COPY.actionVacateDescription,
    danger: true,
  },
  {
    id: 'restart',
    icon: 'refresh',
    label: DELIVERY_OPS_COPY.actionRestart,
    description: DELIVERY_OPS_COPY.actionRestartDescription,
    danger: true,
  },
  {
    id: 'fleet-status',
    icon: 'server',
    label: DELIVERY_OPS_COPY.actionFleetStatus,
    description: DELIVERY_OPS_COPY.actionFleetStatusDescription,
    danger: false,
  },
  {
    id: 'rotate-access',
    icon: 'key',
    label: DELIVERY_OPS_COPY.actionRotateAccess,
    description: DELIVERY_OPS_COPY.actionRotateAccessDescription,
    danger: true,
  },
] as const;

const ACCOUNT_ACTIONS: readonly DeliveryAction[] = [
  {
    id: 'disable-user',
    icon: 'lock',
    label: DELIVERY_OPS_COPY.actionDisableUser,
    description: DELIVERY_OPS_COPY.actionDisableUserDescription,
    danger: true,
  },
  {
    id: 'change-role',
    icon: 'users',
    label: DELIVERY_OPS_COPY.actionChangeRole,
    description: DELIVERY_OPS_COPY.actionChangeRoleDescription,
    danger: false,
  },
] as const;

function fmtTime(ms?: number | null): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  try {
    return new Date(ms).toLocaleTimeString('es-MX');
  } catch {
    return '—';
  }
}

interface ActionRowProps {
  action: DeliveryAction;
  onPick: (a: DeliveryAction) => void;
}

function ActionRow({ action, onPick }: ActionRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'center',
        padding: '14px 20px',
        borderBottom: '1px solid var(--lightest-gray)',
      }}
    >
      <Icon name={action.icon} size={18} color="var(--muted-slate)" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
          {action.label}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>
          {action.description}
        </div>
      </div>
      <Btn
        size="sm"
        variant={action.danger ? 'ghost' : 'solid'}
        decorative
        onClick={() => onPick(action)}
      >
        {DELIVERY_OPS_COPY.confirmModalApply}
      </Btn>
    </div>
  );
}

export function DeliveryOps() {
  // The member hook is consulted so the surface proves it is admin-aware
  // (FR-015 (O-C) structural check; even though AdminGate already redirects,
  // having the hook bound prevents a future regression where the gate is
  // dropped and the body still ships without role awareness).
  useCurrentMember();

  // Real fleet read: delivery points + their facts (capacity, status).
  const nodes = useQuery(api.edgeNodes.listEdgeNodes, {}) as
    | EdgeNodeShape[]
    | undefined;

  // Real recent-tasks read: the operator-facing "recent actions" surrogate.
  // Twin #2: this is api.backgroundTasks.listRecent (the REAL twin), never
  // api.deliveryLogs.listRecent (the STUB twin).
  const recentTasks = useQuery(api.backgroundTasks.listRecent, {}) as
    | TaskSummaryShape[]
    | undefined;

  const [pendingAction, setPendingAction] = useState<DeliveryAction | null>(
    null,
  );

  const isFleetLoading = nodes === undefined;
  const isLogLoading = recentTasks === undefined;

  const onlineCount = (nodes ?? []).filter((n) => n.isOnline).length;
  const totalCount = (nodes ?? []).length;
  const capacityUsedPct = (() => {
    const arr = nodes ?? [];
    let used = 0;
    let cap = 0;
    for (const n of arr) {
      cap += n.streamCapacity ?? 0;
      used += n.currentStreamLoad ?? 0;
    }
    if (cap === 0) return '—';
    return `${Math.round((used / cap) * 100)}%`;
  })();

  const lastActionAt = (() => {
    const arr = recentTasks ?? [];
    let max = 0;
    for (const t of arr) {
      const ts = t.lastRunAt ?? 0;
      if (ts > max) max = ts;
    }
    return max > 0 ? fmtTime(max) : '—';
  })();

  return (
    <AdminGate>
      <PageHead
        title={DELIVERY_OPS_COPY.pageTitle}
        description={DELIVERY_OPS_COPY.pageDescription}
      />

      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          label={DELIVERY_OPS_COPY.statOnlineLabel}
          value={isFleetLoading ? '—' : `${onlineCount} / ${totalCount}`}
          icon="server"
          tone={onlineCount === totalCount && totalCount > 0 ? 'good' : 'warn'}
        />
        <StatCard
          label={DELIVERY_OPS_COPY.statCapacityLabel}
          value={isFleetLoading ? '—' : capacityUsedPct}
          icon="gauge"
        />
        <StatCard
          label={DELIVERY_OPS_COPY.statOpsTodayLabel}
          value={isLogLoading ? '—' : String((recentTasks ?? []).length)}
          icon="bolt"
        />
        <StatCard
          label={DELIVERY_OPS_COPY.statLastOpLabel}
          value={lastActionAt}
          icon="clock"
        />
      </div>

      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          padding: '14px 16px',
          background: 'rgba(220,38,38,0.05)',
          border: '1px solid rgba(220,38,38,0.2)',
          borderRadius: 22,
          marginBottom: 24,
          fontSize: 13,
          color: 'var(--body-muted)',
        }}
      >
        <Icon
          name="alertTriangle"
          size={16}
          color="var(--bad)"
          style={{ marginTop: 1 }}
        />
        {DELIVERY_OPS_COPY.warningProduction}
      </div>

      <div className="circa-cols-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title={DELIVERY_OPS_COPY.panelDeliveryTitle} pad={0}>
            {isFleetLoading ? (
              <div style={{ padding: 16 }}>
                <Skeleton h={160} />
              </div>
            ) : (nodes ?? []).length === 0 ? (
              <EmptyState
                icon="server"
                title={DELIVERY_OPS_COPY.emptyNodesTitle}
                body={DELIVERY_OPS_COPY.emptyNodesBody}
              />
            ) : (
              <>
                {DELIVERY_ACTIONS.map((a) => (
                  <ActionRow
                    key={a.id}
                    action={a}
                    onPick={setPendingAction}
                  />
                ))}
              </>
            )}
          </Panel>

          <Panel title={DELIVERY_OPS_COPY.panelAccountsTitle} pad={0}>
            {ACCOUNT_ACTIONS.map((a) => (
              <ActionRow key={a.id} action={a} onPick={setPendingAction} />
            ))}
          </Panel>
        </div>

        <div
          style={{
            background:
              'linear-gradient(160deg, var(--forest-a), var(--forest-b))',
            borderRadius: 22,
            padding: 22,
            color: '#fff',
            position: 'sticky',
            top: 80,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            <span
              className="t-mono"
              style={{ fontSize: 11, color: 'var(--pale-green)' }}
            >
              {DELIVERY_OPS_COPY.panelLogTitle}
            </span>
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: 'var(--live)',
                animation: 'circa-pulse 1.4s infinite',
              }}
            />
          </div>

          {isLogLoading ? (
            <Skeleton h={160} />
          ) : (recentTasks ?? []).length === 0 ? (
            <div
              style={{
                fontSize: 13,
                color: 'rgba(255,255,255,0.7)',
                padding: '12px 0',
              }}
            >
              <div style={{ fontWeight: 500, marginBottom: 6 }}>
                {DELIVERY_OPS_COPY.emptyLogTitle}
              </div>
              <div style={{ fontSize: 12 }}>
                {DELIVERY_OPS_COPY.emptyLogBody}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(recentTasks ?? []).map((t: TaskSummaryShape, i: number) => {
                const key = (t.taskName ?? 'task') + ':' + i;
                const isLast = i === (recentTasks ?? []).length - 1;
                const ok = (t.lastStatus ?? 'ok') === 'ok';
                const stateLabel = ok
                  ? DELIVERY_OPS_COPY.resultStateOk
                  : DELIVERY_OPS_COPY.resultStateFailed;
                return (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      paddingBottom: 12,
                      borderBottom: isLast
                        ? 'none'
                        : '1px solid rgba(255,255,255,0.12)',
                    }}
                  >
                    <span
                      className="t-mono"
                      style={{
                        fontSize: 10,
                        color: 'rgba(255,255,255,0.55)',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fmtTime(t.lastRunAt)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        className="t-mono"
                        style={{
                          fontSize: 11,
                          textTransform: 'none',
                          color: '#fff',
                          wordBreak: 'break-word',
                        }}
                      >
                        {t.taskName ?? '—'}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: ok ? '#7ee2b8' : '#ffb4a8',
                      }}
                    >
                      {stateLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {pendingAction !== null ? (
        <div
          role="dialog"
          aria-label={pendingAction.label}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.32)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setPendingAction(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 480,
              background: 'var(--bg)',
              borderRadius: 22,
              padding: 24,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 6 }}>
              {pendingAction.label}
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--muted-slate)',
                marginBottom: 14,
              }}
            >
              {pendingAction.description}
            </div>
            <Pill tone="warn" size="sm">
              {DELIVERY_OPS_COPY.confirmModalDoubleCheck}
            </Pill>
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end',
                marginTop: 16,
              }}
            >
              <Btn variant="ghost" onClick={() => setPendingAction(null)}>
                {DELIVERY_OPS_COPY.confirmModalCancel}
              </Btn>
              <Btn variant={pendingAction.danger ? 'danger' : 'solid'} decorative>
                {DELIVERY_OPS_COPY.confirmModalApply}
              </Btn>
            </div>
          </div>
        </div>
      ) : null}
    </AdminGate>
  );
}

export default DeliveryOps;
